import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { capitalize } from 'lodash';

import { Breadcrumb } from '@shared/components/bootstrap';
import { Timestamp, Retry, Picture } from '@shared/components/helpers';
import {
  dispatchDocumentEvent,
  WAREHOUSE_TASK_COUNT_UPDATED_EVENT,
  type WarehouseTaskCountUpdatedEventDetail,
} from '@shared/hooks/use_document_event_listener';
import { ImgixFit } from '@shared/utils/imgix';

import { Panel } from '@admin/components/helpers/panel';
import { Permission } from '@admin/components/helpers/permission';
import { Refresh } from '@admin/components/refresh';
import { Spinner } from '@admin/components/spinner';
import { accountItemURL, accountURL, locationURL, palletURL, warehouseTasksURL } from '@admin/config/routes';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { WAREHOUSE_OPS } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  WarehouseTaskQuery,
  useWarehouseTaskQuery,
  useWarehouseTaskCancelMutation,
  useWarehouseTaskMarkViewedMutation,
  useWarehouseTaskActionCancelMutation,
  useWarehouseTaskActionCompleteMutation,
  useWarehouseTaskActionCommentCreateMutation,
} from '@admin/schema';

import { Comments } from './comments';
import { ACTION_TYPE_LABELS, STATE_LABELS, stateClass } from './constants';

const TEAL = '#17a2b8';

const PhotoThumbnails: React.FC<{ urls: string[] }> = ({ urls }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, position: 'relative' }}>
      {urls.map((url, i) => (
        <span
          key={i}
          style={{ position: 'relative', display: 'inline-block' }}
          onMouseEnter={() => setHoveredIndex(i)}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Picture
              source={url}
              width={24}
              height={24}
              fit={ImgixFit.Fill}
              alt={`Photo ${i + 1}`}
              style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 3, cursor: 'pointer' }}
            />
          </a>
          {hoveredIndex === i && (
            <div
              style={{
                position: 'absolute',
                bottom: 28,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                background: '#fff',
                border: '1px solid #ddd',
                borderRadius: 4,
                padding: 4,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              <Picture
                source={url}
                alt={`Photo ${i + 1}`}
                width={240}
                height={240}
                fit={ImgixFit.Fill}
                style={{ width: 240, height: 240, objectFit: 'contain', display: 'block' }}
              />
            </div>
          )}
        </span>
      ))}
    </span>
  );
};

const subTaskStateClass = (state: string) => {
  switch (state) {
    case 'PENDING':
      return 'label-info';
    case 'IN_PROGRESS':
      return 'label-warning';
    case 'COMPLETED':
      return 'label-success';
    case 'CANCELED':
      return 'label-default';
    default:
      return 'label-info';
  }
};

const formatDuration = (seconds: number | null | undefined) => {
  if (seconds == null) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

type TaskItem = NonNullable<WarehouseTaskQuery['warehouseTask']>['items'][number];
type TaskAction = TaskItem['actions'][number];

export const WarehouseTaskShowContent: React.FC = () => {
  useTitle('Warehouse Task');

  const canUpdateTask = usePermission(WAREHOUSE_OPS.resource, WAREHOUSE_OPS.actions.update);
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, refetch } = useWarehouseTaskQuery({
    client,
    variables: { id },
  });

  const [cancelTask] = useWarehouseTaskCancelMutation({ client });
  const [markViewed] = useWarehouseTaskMarkViewedMutation({ client });
  const [cancelError, setCancelError] = useState<string | null>(null);

  useEffect(() => {
    if (data?.warehouseTask?.unreadActivity) {
      markViewed({ variables: { id } })
        .then(() => refetch())
        .catch(() => {});
    }
  }, [data?.warehouseTask?.unreadActivity, id, markViewed, refetch]);

  const unreadCount = data?.warehouseTaskUnreadCount;

  useEffect(() => {
    if (unreadCount !== undefined) {
      dispatchDocumentEvent<WarehouseTaskCountUpdatedEventDetail>(WAREHOUSE_TASK_COUNT_UPDATED_EVENT, {
        count: unreadCount,
      });
    }
  }, [unreadCount]);

  if (loading && !data) return <Spinner />;
  if (error) return <Retry error={error} refetch={refetch} />;
  if (!data) return null;

  const task = data.warehouseTask;

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this task?')) return;
    try {
      const result = await cancelTask({ variables: { id } });
      const payload = result.data?.warehouseTaskCancel;
      if (payload?.status !== 'OK') {
        setCancelError(payload?.error || 'Failed to cancel task');
        return;
      }
      setCancelError(null);
      refetch();
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Failed to cancel task');
    }
  };

  return (
    <div>
      <div className="page-header text-overflow">
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to={warehouseTasksURL()}>Warehouse Tasks</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Task #{task.id}</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <Panel>
        <Panel.Header>
          {canUpdateTask && task.state !== 'COMPLETED' && task.state !== 'CANCELED' && (
            <Panel.Control>
              <button className="btn btn-danger btn-sm" onClick={handleCancel}>
                Cancel Task
              </button>
              {cancelError && <div style={{ color: 'red', marginTop: 8 }}>{cancelError}</div>}
            </Panel.Control>
          )}
          <Panel.Title>Warehouse Task #{task.id}</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <div className="row">
            <div className="col-md-6">
              <dl>
                <dt>Account</dt>
                <dd>
                  <a href={accountURL({ id: task.account.id })}>{task.account.name}</a>
                </dd>
                <dt>Status</dt>
                <dd>
                  <span className={`label ${stateClass(task.state)}`}>
                    {STATE_LABELS[task.state] || capitalize(task.state.replace(/_/g, ' '))}
                  </span>
                </dd>
              </dl>
            </div>
            <div className="col-md-6">
              <dl>
                <dt>Created By</dt>
                <dd>{task.creator.name}</dd>
                <dt>Due Date</dt>
                <dd>{task.dueDate ? <Timestamp value={task.dueDate} format={Timestamp.Format.DateTime} /> : '-'}</dd>
                <dt>Created</dt>
                <dd>
                  <Timestamp value={task.createdAt} format={Timestamp.Format.DateTime} />
                </dd>
              </dl>
            </div>
          </div>
        </Panel.Body>
      </Panel>

      {task.items.length > 0 && (
        <Panel>
          <Panel.Header>
            <Panel.Title>Items & Actions ({task.items.length})</Panel.Title>
          </Panel.Header>
          <Panel.Body>
            {task.items.map((taskItem) => (
              <TaskItemCard
                key={taskItem.id}
                taskItem={taskItem}
                taskId={task.id}
                accountId={task.account.id}
                onRefetch={refetch}
              />
            ))}
          </Panel.Body>
        </Panel>
      )}

      <Panel>
        <Panel.Header>
          <Panel.Title>Sub-Tasks ({task.subTasks.length})</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          {task.subTasks.map((subTask) => (
            <div
              key={subTask.id}
              style={{
                border: '1px solid #ddd',
                borderRadius: 4,
                padding: 16,
                marginBottom: 12,
              }}
            >
              <div className="row">
                <div className="col-md-4">
                  <strong>{subTask.warehouse.name}</strong>
                  <br />
                  <span className={`label ${subTaskStateClass(subTask.state)}`}>
                    {capitalize(subTask.state.replace(/_/g, ' '))}
                  </span>
                </div>
                <div className="col-md-4">
                  <div>
                    <strong>Claimed by:</strong>{' '}
                    {subTask.assignees.length > 0 ? subTask.assignees.map((a) => a.name).join(', ') : 'Unclaimed'}
                  </div>
                  <div>
                    <strong>Time Spent:</strong> {formatDuration(subTask.timeSpentSeconds)}
                  </div>
                </div>
                <div className="col-md-4">
                  {subTask.startedAt && (
                    <div>
                      <strong>Started:</strong>{' '}
                      <Timestamp value={subTask.startedAt} format={Timestamp.Format.DateTime} />
                    </div>
                  )}
                  {subTask.completedAt && (
                    <div>
                      <strong>Completed:</strong>{' '}
                      <Timestamp value={subTask.completedAt} format={Timestamp.Format.DateTime} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </Panel.Body>
      </Panel>

      <div style={{ margin: '16px 0' }}>
        <Refresh refetch={refetch} intervalSeconds={15} />
      </div>
      <Comments taskId={task.id} comments={task.comments} onCommentAdded={() => refetch()} />
    </div>
  );
};

export const WarehouseTaskShow: React.FC = (props) => (
  <Permission resource={WAREHOUSE_OPS.resource} action={WAREHOUSE_OPS.actions.read}>
    <WarehouseTaskShowContent />
  </Permission>
);

const TaskItemCard: React.FC<{
  taskItem: TaskItem;
  taskId: string;
  accountId: string;
  onRefetch: () => void;
}> = ({ taskItem, taskId, accountId, onRefetch }) => {
  const heroImage = taskItem.item.images[taskItem.item.hero] || taskItem.item.images[0];
  const thumbnailSrc = heroImage?.thumbnailURL;
  const activeActions = taskItem.actions.filter((a) => !a.canceled);
  const completedCount = activeActions.filter((a) => a.completed).length;

  return (
    <div
      style={{
        border: '1px solid #ddd',
        borderRadius: 4,
        marginBottom: 12,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', gap: 16, padding: 16, background: '#fafafa' }}>
        <div style={{ flexShrink: 0, width: 72 }}>
          {thumbnailSrc ? (
            <img
              src={thumbnailSrc}
              alt={taskItem.item.name || ''}
              style={{
                width: 72,
                height: 72,
                objectFit: 'cover',
                borderRadius: 4,
                border: '1px solid #ddd',
              }}
            />
          ) : (
            <div
              style={{
                width: 72,
                height: 72,
                background: '#eee',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontSize: 11,
              }}
            >
              No photo
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div>
            <a href={accountItemURL(accountId, taskItem.item.id)} style={{ fontWeight: 600, fontSize: 14 }}>
              {taskItem.item.barcode?.value || `Item #${taskItem.item.id}`}
            </a>
          </div>
          <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
            {[
              taskItem.item.warehouse?.name,
              capitalize(taskItem.item.state),
              taskItem.item.cuft ? `${parseFloat(taskItem.item.cuft.toFixed(2))} cuft` : null,
              taskItem.item.category?.name,
            ]
              .filter(Boolean)
              .join(' \u00b7 ')}
          </div>
          {taskItem.item.pallet && (
            <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
              <a href={palletURL(taskItem.item.pallet.id)} style={{ color: '#888' }}>
                {taskItem.item.pallet.barcode?.value || `Pallet #${taskItem.item.pallet.id}`}
              </a>
              {' \u00b7 '}
              {capitalize(taskItem.item.pallet.state.replace(/_/g, ' '))}
              {taskItem.item.pallet.location && (
                <>
                  {' @ '}
                  <a href={locationURL(taskItem.item.pallet.location.id)} style={{ color: '#888' }}>
                    {taskItem.item.pallet.location.value}
                  </a>
                </>
              )}
            </div>
          )}
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className={`label ${completedCount === activeActions.length ? 'label-success' : 'label-info'}`}>
              {completedCount}/{activeActions.length} actions complete
            </span>
            {taskItem.item.pallet?.pendingMoveToDock && (
              <span className="label label-warning" style={{ fontSize: 10 }}>
                Pallet Pending Move to Dock
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 8px' }}>
        {taskItem.actions.map((action) => (
          <ActionRow key={action.id} action={action} taskId={taskId} onRefetch={onRefetch} />
        ))}
      </div>
    </div>
  );
};

const ActionRow: React.FC<{
  action: TaskAction;
  taskId: string;
  onRefetch: () => void;
}> = ({ action, taskId, onRefetch }) => {
  const canUpdateTask = usePermission(WAREHOUSE_OPS.resource, WAREHOUSE_OPS.actions.update);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentBody, setCommentBody] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  const [cancelAction, { loading: cancelLoading }] = useWarehouseTaskActionCancelMutation({ client });
  const [completeAction, { loading: completeLoading }] = useWarehouseTaskActionCompleteMutation({ client });
  const [createComment] = useWarehouseTaskActionCommentCreateMutation({ client });
  const [actionCancelError, setActionCancelError] = useState<string | null>(null);
  const [actionCompleteError, setActionCompleteError] = useState<string | null>(null);

  const handleCancel = async () => {
    if (!window.confirm('Cancel this action? It will no longer need to be completed.')) return;
    try {
      await cancelAction({ variables: { id: action.id } });
      setActionCancelError(null);
      onRefetch();
    } catch (err) {
      setActionCancelError(err instanceof Error ? err.message : 'Failed to cancel action');
    }
  };

  const handleComplete = async () => {
    if (!window.confirm('Mark this action as complete?')) return;
    try {
      await completeAction({ variables: { input: { id: action.id } } });
      setActionCompleteError(null);
      onRefetch();
    } catch (err) {
      setActionCompleteError(err instanceof Error ? err.message : 'Failed to complete action');
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentBody.trim()) return;
    setSubmittingComment(true);
    try {
      await createComment({
        variables: { warehouseTaskActionID: action.id, body: commentBody.trim() },
      });
      setCommentError(null);
      setCommentBody('');
      setShowCommentInput(false);
      onRefetch();
    } catch (err) {
      setCommentError(err instanceof Error ? err.message : 'Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const statusBadge = () => {
    if (action.canceled)
      return (
        <span className="label label-default" style={{ fontSize: 12 }}>
          Canceled
        </span>
      );
    if (action.completed)
      return (
        <span className="label label-success" style={{ fontSize: 12 }}>
          Done
        </span>
      );
    return (
      <span className="label label-warning" style={{ fontSize: 12 }}>
        Pending
      </span>
    );
  };

  return (
    <div
      style={{
        padding: '10px 0',
        borderBottom: '1px solid #eee',
        opacity: action.canceled ? 0.6 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flexShrink: 0, marginTop: 2 }}>{statusBadge()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 600, textDecoration: action.canceled ? 'line-through' : undefined }}>
              {ACTION_TYPE_LABELS[action.actionType] || capitalize(action.actionType.replace(/_/g, ' '))}
            </span>
            {canUpdateTask && !action.completed && !action.canceled && (
              <>
                <button
                  type="button"
                  className="btn btn-xs btn-success"
                  onClick={handleComplete}
                  disabled={completeLoading || cancelLoading}
                  title="Mark this action as complete"
                >
                  {completeLoading ? 'Completing...' : 'Complete'}
                </button>
                <button
                  type="button"
                  className="btn btn-xs btn-default"
                  onClick={handleCancel}
                  disabled={cancelLoading || completeLoading}
                  title="Cancel this action"
                >
                  {cancelLoading ? 'Canceling...' : 'Cancel'}
                </button>
              </>
            )}
            {actionCancelError && <div style={{ color: 'red', fontSize: 12 }}>{actionCancelError}</div>}
            {actionCompleteError && <div style={{ color: 'red', fontSize: 12 }}>{actionCompleteError}</div>}
          </div>

          {action.completed && action.data && Object.keys(action.data).length > 0 && (
            <div style={{ marginTop: 2, color: '#555' }}>
              {action.data.length != null && action.data.width != null && action.data.height != null && (
                <span>
                  Dimensions: {action.data.length}&Prime; &times; {action.data.width}&Prime; &times;{' '}
                  {action.data.height}&Prime;
                </span>
              )}
              {action.data.photo_urls && <PhotoThumbnails urls={action.data.photo_urls} />}
              {(action.data.item_category_name ||
                action.data.part_category_name ||
                action.data.material_category_name ||
                action.data.category_name) && (
                <span>
                  Category:{' '}
                  {action.data.item_category_name ||
                    action.data.part_category_name ||
                    action.data.material_category_name ||
                    action.data.category_name}
                </span>
              )}
            </div>
          )}

          {action.comments.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {action.comments.map((comment) => (
                <div
                  key={comment.id}
                  style={{
                    padding: '4px 10px',
                    marginBottom: 4,
                    background: '#f5f5f5',
                    borderLeft: `3px solid ${TEAL}`,
                    borderRadius: 2,
                    fontSize: 13,
                  }}
                >
                  <strong style={{ fontSize: 12 }}>{comment.user.name}</strong>
                  <span className="text-muted" style={{ fontSize: 11, marginLeft: 8 }}>
                    <Timestamp value={comment.createdAt} format={Timestamp.Format.DateTime} />
                  </span>
                  <div style={{ color: '#555', marginTop: 2, whiteSpace: 'pre-wrap' }}>{comment.body}</div>
                </div>
              ))}
            </div>
          )}

          {canUpdateTask && !action.canceled && (
            <div style={{ marginTop: 4 }}>
              {showCommentInput ? (
                <>
                  <form onSubmit={handleCommentSubmit} style={{ display: 'flex', gap: 6, maxWidth: 500 }}>
                    <input
                      type="text"
                      className="form-control input-sm"
                      placeholder="Write a comment..."
                      aria-label="Write a comment"
                      value={commentBody}
                      onChange={(e) => setCommentBody(e.target.value)}
                      autoFocus
                    />
                    <button
                      type="submit"
                      className="btn btn-xs btn-primary"
                      disabled={submittingComment || !commentBody.trim()}
                    >
                      Post
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-default"
                      onClick={() => {
                        setShowCommentInput(false);
                        setCommentBody('');
                      }}
                    >
                      Cancel
                    </button>
                  </form>
                  {commentError && <div style={{ color: 'red', marginTop: 6 }}>{commentError}</div>}
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-xs btn-link"
                  style={{ padding: 0, color: TEAL }}
                  onClick={() => setShowCommentInput(true)}
                >
                  Add comment
                </button>
              )}
            </div>
          )}

          <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
            {action.canceled && action.canceledBy && (
              <span>
                Canceled by {action.canceledBy.name}
                {action.canceledAt && (
                  <>
                    {' '}
                    at <Timestamp value={action.canceledAt} format={Timestamp.Format.DateTime} />
                  </>
                )}
              </span>
            )}
            {action.startedAt && !action.canceled && (
              <span>
                Started: <Timestamp value={action.startedAt} format={Timestamp.Format.DateTime} />
              </span>
            )}
            {action.completedBy && (
              <span style={{ marginLeft: action.startedAt ? 12 : 0 }}>
                Completed by {action.completedBy.name}
                {action.completedAt && (
                  <>
                    {' '}
                    at <Timestamp value={action.completedAt} format={Timestamp.Format.DateTime} />
                  </>
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
